import type { DiagnosticBundleReadModel } from "@billiard/contracts";
import { Download, RefreshCw, ShieldCheck } from "lucide-react";
import { useState } from "react";
import { getSupportDiagnosticBundle } from "./api";

function downloadDiagnostic(bundle: DiagnosticBundleReadModel): void {
  const url = URL.createObjectURL(
    new Blob([`${JSON.stringify(bundle, null, 2)}\n`], { type: "application/json;charset=utf-8" }),
  );
  const anchor = document.createElement("a");
  anchor.href = url;
  anchor.download = "billiard-erp-redacted-diagnostic.json";
  anchor.click();
  URL.revokeObjectURL(url);
}

export function SupportPortal(): React.JSX.Element {
  const [accessToken, setAccessToken] = useState("");
  const [bundle, setBundle] = useState<DiagnosticBundleReadModel | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const inspect = async (): Promise<void> => {
    setBusy(true);
    setError(null);
    setBundle(null);
    try {
      setBundle(await getSupportDiagnosticBundle(accessToken));
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : "授权没有通过");
    } finally {
      setBusy(false);
    }
  };

  return (
    <main className="support-portal">
      <section className="support-portal-card">
        <div className="support-portal-heading">
          <ShieldCheck size={26} aria-hidden="true" />
          <div>
            <span>Billiard OS · 独立入口</span>
            <h1>技术支持诊断</h1>
          </div>
        </div>
        <p>输入老板本次提供的限时授权码。这里只显示脱敏运行状态，不能查看经营资料或执行修改。</p>
        <label>
          <span>限时授权码</span>
          <input
            type="password"
            autoComplete="off"
            value={accessToken}
            placeholder="bsp_…"
            onChange={(event) => setAccessToken(event.target.value)}
          />
        </label>
        <button
          type="button"
          className="primary-button"
          disabled={busy || !/^bsp_[A-Za-z0-9_-]{43}$/u.test(accessToken.trim())}
          onClick={() => void inspect()}
        >
          {busy ? "正在核对授权…" : "读取脱敏诊断"}
        </button>
        {error ? (
          <div className="error-band" role="alert">
            {error}
          </div>
        ) : null}

        {bundle ? (
          <section className="support-diagnostic" aria-live="polite">
            <div className="support-diagnostic-summary">
              <div>
                <strong>
                  {bundle.system.overall === "HEALTHY" ? "门店组件运行正常" : "部分组件需要排查"}
                </strong>
                <span>
                  生成时间：
                  {new Date(bundle.generatedAt).toLocaleString("zh-CN", { hour12: false })}
                </span>
              </div>
              <button type="button" onClick={() => void inspect()}>
                <RefreshCw size={16} aria-hidden="true" /> 刷新
              </button>
            </div>
            <ul>
              {bundle.system.components.map((component) => (
                <li key={component.component}>
                  <span>{component.component}</span>
                  <strong>{component.health}</strong>
                  <small>
                    v{component.productVersion} · 数据结构 {component.schemaVersion || "—"}
                  </small>
                </li>
              ))}
            </ul>
            <p>
              待同步 {bundle.system.storage.pendingOutbox} 条 · 同步失败{" "}
              {bundle.system.storage.failedOutbox} 条 · 待人工处理{" "}
              {bundle.system.storage.pendingManualReview} 条
            </p>
            <button
              type="button"
              className="secondary-button"
              onClick={() => downloadDiagnostic(bundle)}
            >
              <Download size={16} aria-hidden="true" /> 下载脱敏诊断文件
            </button>
          </section>
        ) : null}
      </section>
    </main>
  );
}
